import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import AccountingShell from "./AccountingShell";
import toast from "react-hot-toast";

const INR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const ACC = (v = "") => String(v || "").toUpperCase();

const fmtDate = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime())
      ? d.toLocaleDateString("en-IN")
      : "—";
  } catch {
    return "—";
  }
};

const toDateInput = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : new Date();
    return d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : "";
  } catch {
    return "";
  }
};

const getTime = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  } catch {
    return 0;
  }
};

const getFY = (dateValue) => {
  const d = dateValue ? new Date(dateValue) : new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 4 ? y : y - 1;
  return `${String(start).slice(-2)}${String(start + 1).slice(-2)}`;
};

const makeEMDVoucherNo = (appId = "") =>
  `EMD/${getFY()}/${String(appId).slice(0, 6).toUpperCase()}`;

const makeBGVoucherNo = (appId = "") =>
  `BG/${getFY()}/${String(appId).slice(0, 6).toUpperCase()}`;

const makeRefundVoucherNo = (appId = "") =>
  `EMD-REF/${getFY()}/${String(appId).slice(0, 6).toUpperCase()}`;

const isHeldOnly = (r) =>
  ACC(r.emdAccountingStatus).includes("HELD") &&
  !ACC(r.emdAccountingStatus).includes("BG") &&
  !ACC(r.emdAccountingStatus).includes("REFUNDED");

const isBG = (r) => ACC(r.emdAccountingStatus).includes("BG");
const isRefunded = (r) => ACC(r.emdAccountingStatus).includes("REFUNDED");

const isVerified = (r) =>
  Boolean(r.emdAccountantVerified) ||
  Boolean(r.emdVerifiedFromAccounting) ||
  ACC(r.emdAccountingStatus).includes("VERIFIED") ||
  isHeldOnly(r) ||
  isBG(r) ||
  isRefunded(r);

function buildFullProjectDetail(a = {}) {
  const title = a.tenderTitle || a.projectName || "";
  if (title && title.includes("Construction of")) return title;

  return (
    title ||
    `Construction of 1000 MT Godown under “The World’s Largest Grain Storage Project” Scheme, executed through Shine Agri Allied & Marketing Multistate Cooperative Society Limited (PMC Agency)`
  );
}

function Badge({ children }) {
  const s = String(children || "").toUpperCase();
  let cls = "bg-amber-100 text-amber-700";

  if (
    s.includes("PAID") ||
    s.includes("HELD") ||
    s.includes("BG") ||
    s.includes("POSTED") ||
    s.includes("VERIFIED") ||
    s.includes("REFUNDED") ||
    s.includes("AWARDED")
  ) {
    cls = "bg-emerald-100 text-emerald-700";
  }

  if (
    s.includes("PENDING") ||
    s.includes("NOT_PAID") ||
    s.includes("UNPAID") ||
    s.includes("MISSING")
  ) {
    cls = "bg-amber-100 text-amber-700";
  }

  if (s.includes("CORRECTION") || s.includes("REJECT")) {
    cls = "bg-red-100 text-red-700";
  }

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-[10px] font-black ${cls}`}>
      {children || "—"}
    </span>
  );
}

export default function TenderFeeEMD() {
  return (
    <AccountingShell title="EMD Accounting">
      <EMDAccountingContent />
    </AccountingShell>
  );
}

function EMDAccountingContent({ me = {} }) {
  const [apps, setApps] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [fyFilter, setFyFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("NEWEST");

  const load = async () => {
    const [appSnap, tenderSnap] = await Promise.all([
      getDocs(collection(db, "tender_applications")),
      getDocs(collection(db, "tenders")),
    ]);

    setApps(appSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setTenders(tenderSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    let list = apps.map((a) => {
      const tender = tenders.find((t) => t.id === a.tenderId) || {};
      const emdAmount = Number(a.emdAmount || 0);

      const tenderTitle =
        tender.title ||
        tender.projectName ||
        tender.workName ||
        a.tenderTitle ||
        a.projectName ||
        "—";

      const projectName =
        tender.ownerCompany ||
        tender.pacsName ||
        tender.projectName ||
        a.pacsName ||
        a.projectName ||
        "—";

      const emdDate =
        a.emdVerifiedDate ||
        a.emdPaymentDate ||
        a.emdPayment?.paidAt ||
        a.emdPayment?.date ||
        a.createdAt ||
        a.appliedAt ||
        a.submittedAt;

      return {
        ...a,
        tenderTitle,
        projectName,
        fullProjectDetail: buildFullProjectDetail({
          ...a,
          tenderTitle,
          projectName,
        }),

        tenderAmount: Number(tender.baseAmount || tender.amount || 0),
        l1Amount: Number(
          tender.awardedAmount ||
            tender.tenderAwardAmount ||
            tender.awardedValue ||
            a.l1Amount ||
            0
        ),

        isAwarded:
          Boolean(a.isAwarded) ||
          Boolean(a.awarded) ||
          ACC(a.awardStatus) === "AWARDED" ||
          String(tender.awardedVendorUid || "") === String(a.vendorUid || ""),

        vendorDisplay:
          a.vendorName || a.companyName || a.contractorName || a.vendorUid || "—",

        emdAmount,
        emdStatus: String(a.emdStatus || "pending").toLowerCase(),
        emdAccountingStatus: a.emdAccountingStatus || "PENDING",
        emdAccountantVerified: Boolean(a.emdAccountantVerified),

        emdDate,
        emdFY: a.emdFY || getFY(toDateInput(emdDate)),
        emdVoucherNo: a.emdVoucherNo || makeEMDVoucherNo(a.id),
        emdPaymentReference:
          a.emdPaymentReference || a.emdPayment?.reference || "",

        emdBgReference: a.emdBgReference || "",
        emdBgDate: a.emdBgDate || a.emdMarkedAsBGAt || a.bgDate || "",
        emdBgVoucherNo: a.emdBgVoucherNo || makeBGVoucherNo(a.id),

        emdRefundUtr: a.emdRefundUtr || "",
        emdRefundAmount: Number(a.emdRefundAmount || 0),
        emdRefundDate: a.emdRefundDate || a.emdRefundedAt || "",
        emdRefundVoucherNo: a.emdRefundVoucherNo || makeRefundVoucherNo(a.id),

        createdTime: getTime(a.createdAt || a.appliedAt || a.submittedAt),
      };
    });

    if (fyFilter !== "ALL") list = list.filter((r) => r.emdFY === fyFilter);

    if (statusFilter !== "ALL") {
      list = list.filter((r) => {
        const acc = ACC(r.emdAccountingStatus);

        if (statusFilter === "EMD_PENDING") return r.emdStatus === "pending";
        if (statusFilter === "EMD_PAID") return r.emdStatus === "paid";
        if (statusFilter === "EMD_NOT_PAID") return r.emdStatus === "not_paid";
        if (statusFilter === "ACCOUNTANT_VERIFIED") return isVerified(r);
        if (statusFilter === "EMD_HELD") return isHeldOnly(r);
        if (statusFilter === "MARKED_AS_BG_SECURITY") return isBG(r);
        if (statusFilter === "EMD_REFUNDED") return isRefunded(r);

        return acc === statusFilter;
      });
    }

    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        [
          r.vendorDisplay,
          r.vendorUid,
          r.tenderId,
          r.tenderTitle,
          r.projectName,
          r.fullProjectDetail,
          r.id,
          r.emdPaymentReference,
          r.emdBgReference,
          r.emdRefundUtr,
          r.emdVoucherNo,
          r.emdBgVoucherNo,
          r.emdRefundVoucherNo,
        ]
          .join(" ")
          .toLowerCase()
          .includes(s)
      );
    }

    if (sortBy === "NEWEST") list.sort((a, b) => b.createdTime - a.createdTime);
    if (sortBy === "OLDEST") list.sort((a, b) => a.createdTime - b.createdTime);
    if (sortBy === "AMOUNT_HIGH") list.sort((a, b) => b.emdAmount - a.emdAmount);

    return list;
  }, [apps, tenders, search, statusFilter, fyFilter, sortBy]);

  const fyOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.emdFY).filter(Boolean));
    return ["ALL", ...Array.from(set).sort().reverse()];
  }, [rows]);

  const totals = useMemo(
    () => ({
      total: rows.length,
      pending: rows.filter((r) => r.emdStatus === "pending").length,
      notPaid: rows.filter((r) => r.emdStatus === "not_paid").length,
      emdTotal: rows.reduce((s, r) => s + Number(r.emdAmount || 0), 0),
      emdPaid: rows.filter((r) => r.emdStatus === "paid").length,
      emdHeld: rows.filter((r) => isHeldOnly(r)).length,
      bgConverted: rows.filter((r) => isBG(r)).length,
      refunded: rows.filter((r) => isRefunded(r)).length,
      heldAmount: rows
        .filter((r) => isHeldOnly(r))
        .reduce((s, r) => s + Number(r.emdAmount || 0), 0),
      bgAmount: rows
        .filter((r) => isBG(r))
        .reduce((s, r) => s + Number(r.emdAmount || 0), 0),
    }),
    [rows]
  );

  const safeAddLedger = async (payload) => {
    const q = query(
      collection(db, "contractor_ledgers"),
      where("applicationId", "==", payload.applicationId),
      where("type", "==", payload.type),
      where("voucherNo", "==", payload.voucherNo)
    );

    const snap = await getDocs(q);
    if (!snap.empty) return false;

    await addDoc(collection(db, "contractor_ledgers"), {
      ...payload,
      createdAt: serverTimestamp(),
      createdBy: me.uid || "",
      createdByRole: me.role || "",
    });

    return true;
  };

  const accountantOnly = () => {
    if (!["ACCOUNTANT", "ADMIN", "CEO"].includes(me.role)) {
      toast.error("Only Accountant/Admin/CEO allowed.");
      return false;
    }
    return true;
  };

  const editEMDDetails = async (a) => {
    if (!accountantOnly()) return;

    const amountText = window.prompt("EMD Amount:", String(a.emdAmount || 0));
    if (!amountText) return;

    const paymentDate = window.prompt(
      "EMD Payment Date YYYY-MM-DD:",
      toDateInput(a.emdDate)
    );
    if (!paymentDate) return;

    const paymentRef = window.prompt(
      "EMD Payment Ref / UTR:",
      a.emdPaymentReference || ""
    );

    const amount = Number(amountText);
    const voucherNo = a.emdVoucherNo || makeEMDVoucherNo(a.id);

    await updateDoc(doc(db, "tender_applications", a.id), {
      emdAmount: amount,
      emdVoucherNo: voucherNo,
      emdFY: getFY(paymentDate),
      emdPaymentDate: paymentDate,
      emdPaymentReference: paymentRef || "",
      emdEditedBy: me.uid || "",
      emdEditedAt: serverTimestamp(),
    });

    toast.success("EMD detail updated.");
    load();
  };

  const verifyEMDPaid = async (a) => {
    if (!accountantOnly()) return;

    const amountText = window.prompt("Verify EMD amount:", String(a.emdAmount || 0));
    if (!amountText) return;

    const paymentDate = window.prompt(
      "EMD Payment Date YYYY-MM-DD:",
      toDateInput(a.emdDate || new Date())
    );
    if (!paymentDate) return;

    const refNo = window.prompt(
      "EMD Payment Ref / UTR:",
      a.emdPaymentReference || "payment done"
    );

    const amount = Number(amountText);
    const voucherNo = a.emdVoucherNo || makeEMDVoucherNo(a.id);

    const nextAccountingStatus = isBG(a)
      ? "MARKED_AS_BG_SECURITY"
      : isRefunded(a)
      ? "EMD_REFUNDED"
      : isHeldOnly(a)
      ? "EMD_HELD"
      : "ACCOUNTANT_VERIFIED";

    await updateDoc(doc(db, "tender_applications", a.id), {
      emdAmount: amount,
      emdStatus: "paid",
      emdVoucherNo: voucherNo,
      emdFY: getFY(paymentDate),
      emdPaymentDate: paymentDate,
      emdPaymentReference: refNo || "",
      emdAccountantVerified: true,
      emdVerifiedFromAccounting: true,
      emdVerifiedBy: me.uid || "",
      emdVerifiedAt: serverTimestamp(),
      emdAccountingStatus: nextAccountingStatus,
      emdAccountingRemark: "",
    });

    toast.success("EMD accountant verification completed.");
    load();
  };

  const markEMDNotPaid = async (a) => {
    if (!accountantOnly()) return;

    const remark = window.prompt(
      "Not paid remark:",
      "EMD not received / pending from vendor"
    );
    if (!remark) return;

    await updateDoc(doc(db, "tender_applications", a.id), {
      emdStatus: "not_paid",
      emdAccountingStatus: "EMD_NOT_PAID",
      emdAccountantVerified: false,
      emdNotPaidRemark: remark,
      emdNotPaidMarkedBy: me.uid || "",
      emdNotPaidMarkedAt: serverTimestamp(),
    });

    toast.success("EMD marked as not paid.");
    load();
  };

  const markEMDHeld = async (a) => {
    if (!accountantOnly()) return;

    if (a.emdStatus !== "paid") {
      toast.error("Pehle EMD payment verify karo.");
      return;
    }

    if (!isVerified(a)) {
      toast.error("Pehle Accountant Verify karo.");
      return;
    }

    if (isHeldOnly(a) || isBG(a) || isRefunded(a)) {
      toast.error("Ye EMD already held/BG/refunded status me hai.");
      return;
    }

    const voucherNo = a.emdVoucherNo || makeEMDVoucherNo(a.id);
    const amount = Number(a.emdAmount || 0);
    const fullProjectDetail = a.fullProjectDetail || buildFullProjectDetail(a);

    await updateDoc(doc(db, "tender_applications", a.id), {
      emdAccountingStatus: "EMD_HELD",
      emdLedgerPosted: true,
      emdHeldAt: serverTimestamp(),
      emdHeldBy: me.uid || "",
      emdVoucherNo: voucherNo,
    });

    await safeAddLedger({
      vendorUid: a.vendorUid || "",
      vendorName: a.vendorDisplay || "",
      tenderId: a.tenderId || "",
      applicationId: a.id,
      voucherNo,
      type: "EMD_RECEIVED_HELD",
      debit: 0,
      credit: amount,
      amount,
      emdAmount: amount,
      referenceNo: a.emdPaymentReference || "",
      paymentDate: a.emdPaymentDate || a.emdDate || "",
      emdPaymentDate: a.emdPaymentDate || a.emdDate || "",
      fullProjectDetail,
      projectDetail: fullProjectDetail,
      tenderTitle: a.tenderTitle,
      projectName: a.projectName,
      pacsName: a.projectName,
      narration: `EMD received and held as refundable security - ${fullProjectDetail}`,
      serviceName: "EMD Security Held",
      status: "POSTED",
    });

    toast.success("EMD held ledger me credit post ho gaya.");
    load();
  };

  const convertToBG = async (a) => {
    if (!accountantOnly()) return;

    if (a.emdStatus !== "paid") {
      toast.error("EMD paid nahi hai.");
      return;
    }

    if (!isVerified(a)) {
      toast.error("Pehle Accountant Verify karo.");
      return;
    }

    if (!a.isAwarded) {
      toast.error("Only awarded vendor ka EMD BG me convert hoga.");
      return;
    }

    if (!isHeldOnly(a)) {
      toast.error("Pehle EMD ko Held karo, uske baad BG me convert karo.");
      return;
    }

    const bgRef = window.prompt("BG / Security Reference No:", a.emdBgReference || "");
    if (!bgRef) return;

    const bgDate = window.prompt("BG Date YYYY-MM-DD:", toDateInput(new Date()));
    if (!bgDate) return;

    const amount = Number(a.emdAmount || 0);
    const voucherNo = a.emdBgVoucherNo || makeBGVoucherNo(a.id);
    const fullProjectDetail = a.fullProjectDetail || buildFullProjectDetail(a);

    await updateDoc(doc(db, "tender_applications", a.id), {
      emdAccountingStatus: "MARKED_AS_BG_SECURITY",
      emdAccountantVerified: true,
      emdVerifiedFromAccounting: true,
      emdBgReference: bgRef,
      emdBgDate: bgDate,
      emdBgVoucherNo: voucherNo,
      emdBgLedgerPosted: true,
      emdMarkedAsBGAt: serverTimestamp(),
      emdMarkedAsBGBy: me.uid || "",
    });

    await safeAddLedger({
      vendorUid: a.vendorUid || "",
      vendorName: a.vendorDisplay || "",
      tenderId: a.tenderId || "",
      applicationId: a.id,
      voucherNo,
      type: "BG_SECURITY_HELD",
      debit: 0,
      credit: amount,
      amount,
      emdAmount: amount,
      bgAmount: amount,
      referenceNo: bgRef,
      emdBgDate: bgDate,
      bgDate,
      fullProjectDetail,
      projectDetail: fullProjectDetail,
      tenderTitle: a.tenderTitle,
      projectName: a.projectName,
      pacsName: a.projectName,
      narration: `EMD converted and held as BG Security - ${fullProjectDetail}. BG Ref: ${bgRef}`,
      serviceName: "BG Security Held",
      status: "POSTED",
    });

    toast.success("BG Security ledger me credit post ho gaya.");
    load();
  };

  const refundEMD = async (a) => {
    if (!accountantOnly()) return;

    if (a.emdStatus !== "paid") {
      toast.error("EMD paid nahi hai.");
      return;
    }

    if (!isVerified(a)) {
      toast.error("Pehle Accountant Verify karo.");
      return;
    }

    if (a.isAwarded) {
      toast.error("Awarded vendor ka EMD refund nahi, BG convert karo.");
      return;
    }

    if (isBG(a)) {
      toast.error("BG marked EMD refund nahi ho sakta.");
      return;
    }

    const utr = window.prompt("Refund UTR / Reference No:");
    if (!utr) return;

    const refundDate = window.prompt("Refund Date YYYY-MM-DD:", toDateInput(new Date()));
    if (!refundDate) return;

    const amountText = window.prompt("Refund Amount:", String(a.emdAmount || 0));
    if (!amountText) return;

    const amount = Number(amountText);
    const voucherNo = a.emdRefundVoucherNo || makeRefundVoucherNo(a.id);
    const fullProjectDetail = a.fullProjectDetail || buildFullProjectDetail(a);

    await updateDoc(doc(db, "tender_applications", a.id), {
      emdAccountingStatus: "EMD_REFUNDED",
      emdAccountantVerified: true,
      emdVerifiedFromAccounting: true,
      emdRefundAmount: amount,
      emdRefundUtr: utr,
      emdRefundDate: refundDate,
      emdRefundVoucherNo: voucherNo,
      emdRefundLedgerPosted: true,
      emdRefundedAt: serverTimestamp(),
      emdRefundedBy: me.uid || "",
    });

    await safeAddLedger({
      vendorUid: a.vendorUid || "",
      vendorName: a.vendorDisplay || "",
      tenderId: a.tenderId || "",
      applicationId: a.id,
      voucherNo,
      referenceNo: utr,
      type: "EMD_REFUND",
      debit: amount,
      credit: 0,
      amount,
      emdAmount: amount,
      paymentDate: refundDate,
      emdRefundDate: refundDate,
      fullProjectDetail,
      projectDetail: fullProjectDetail,
      tenderTitle: a.tenderTitle,
      projectName: a.projectName,
      pacsName: a.projectName,
      narration: `EMD refunded to contractor - ${fullProjectDetail}. UTR: ${utr}`,
      paymentUtr: utr,
      serviceName: "EMD Refund",
      status: "POSTED",
    });

    toast.success("EMD refund debit posted.");
    load();
  };

  const correction = async (a) => {
    if (!accountantOnly()) return;

    const remark = window.prompt("Correction remark:");
    if (!remark) return;

    await updateDoc(doc(db, "tender_applications", a.id), {
      emdAccountingStatus: "CORRECTION_REQUIRED",
      emdAccountingRemark: remark,
      emdCorrectionRaisedAt: serverTimestamp(),
      emdCorrectionRaisedBy: me.uid || "",
    });

    toast.success("Correction marked.");
    load();
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 xl:grid-cols-8 gap-3">
        <Card title="Applications" value={totals.total} />
        <Card title="Pending" value={totals.pending} red />
        <Card title="Not Paid" value={totals.notPaid} red />
        <Card title="EMD Paid" value={totals.emdPaid} green />
        <Card title="EMD Held" value={totals.emdHeld} green />
        <Card title="BG Converted" value={totals.bgConverted} green />
        <Card title="Refunded" value={totals.refunded} red />
        <Card title="Total EMD" value={INR(totals.emdTotal)} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border p-4">
          <p className="text-xs text-slate-500 font-bold">EMD Held Amount</p>
          <h2 className="text-xl font-black text-emerald-700 mt-1">
            {INR(totals.heldAmount)}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-4">
          <p className="text-xs text-slate-500 font-bold">BG Security Amount</p>
          <h2 className="text-xl font-black text-blue-700 mt-1">
            {INR(totals.bgAmount)}
          </h2>
        </div>
      </div>

      <div className="bg-white rounded-2xl border p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            className="input"
            placeholder="Search vendor / tender / EMD ref / BG / refund..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            className="input"
            value={fyFilter}
            onChange={(e) => setFyFilter(e.target.value)}
          >
            {fyOptions.map((f) => (
              <option key={f} value={f}>
                {f === "ALL" ? "All FY" : `FY ${f}`}
              </option>
            ))}
          </select>

          <select
            className="input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Status</option>
            <option value="EMD_PENDING">EMD Pending</option>
            <option value="EMD_NOT_PAID">EMD Not Paid</option>
            <option value="EMD_PAID">EMD Paid</option>
            <option value="ACCOUNTANT_VERIFIED">Accountant Verified</option>
            <option value="EMD_HELD">EMD Held</option>
            <option value="MARKED_AS_BG_SECURITY">BG Security</option>
            <option value="EMD_REFUNDED">EMD Refunded</option>
            <option value="CORRECTION_REQUIRED">Correction</option>
          </select>

          <select
            className="input"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="NEWEST">Newest</option>
            <option value="OLDEST">Oldest</option>
            <option value="AMOUNT_HIGH">Amount High</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border p-4">
        <h2 className="text-lg font-black text-center mb-1">
          EMD Accounting Register
        </h2>
        <p className="text-xs text-slate-500 text-center mb-4">
          EMD invoice nahi banega | Accountant verify / not-paid / held / BG / refund karega
        </p>

        <div className="overflow-auto">
          <table className="min-w-[1900px] w-full text-[11px] border border-slate-300">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="p-2 border">Sr</th>
                <th className="p-2 border text-left">Application</th>
                <th className="p-2 border text-left">Vendor</th>
                <th className="p-2 border text-left">Tender / Project</th>
                <th className="p-2 border text-right">Tender Amount</th>
                <th className="p-2 border text-right">L1 Amount</th>
                <th className="p-2 border text-right">EMD</th>
                <th className="p-2 border text-left">EMD Voucher / Date</th>
                <th className="p-2 border text-left">Payment Ref</th>
                <th className="p-2 border text-left">BG Detail</th>
                <th className="p-2 border text-left">Refund Detail</th>
                <th className="p-2 border text-center">Award</th>
                <th className="p-2 border text-center">EMD Status</th>
                <th className="p-2 border text-center">Accounting</th>
                <th className="p-2 border text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((a, i) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="p-2 border text-center">{i + 1}</td>

                  <td className="p-2 border font-black">
                    {a.id}
                    <div className="text-[10px] text-slate-500">
                      {fmtDate(a.createdAt || a.appliedAt)}
                    </div>
                  </td>

                  <td className="p-2 border font-black">
                    {a.vendorDisplay}
                    <div className="text-[10px] text-slate-500">
                      UID: {a.vendorUid || "—"}
                    </div>
                  </td>

                  <td className="p-2 border">
                    <div className="font-bold">{a.fullProjectDetail}</div>
                    <div className="text-[10px] text-slate-500 mt-1">{a.projectName}</div>
                  </td>

                  <td className="p-2 border text-right">{INR(a.tenderAmount)}</td>
                  <td className="p-2 border text-right font-black">{INR(a.l1Amount)}</td>
                  <td className="p-2 border text-right font-black">{INR(a.emdAmount)}</td>

                  <td className="p-2 border font-black">
                    {a.emdVoucherNo || "—"}
                    <div className="text-[10px] text-slate-500">{fmtDate(a.emdDate)}</div>
                    <div className="text-[10px] text-slate-500">FY {a.emdFY || "—"}</div>
                  </td>

                  <td className="p-2 border">{a.emdPaymentReference || "—"}</td>

                  <td className="p-2 border">
                    <div className="font-bold">{a.emdBgReference || "—"}</div>
                    {a.emdBgReference && (
                      <div className="text-[10px] text-slate-500">
                        {a.emdBgVoucherNo} | {fmtDate(a.emdBgDate)}
                      </div>
                    )}
                  </td>

                  <td className="p-2 border">
                    <div className="font-bold">
                      {a.emdRefundUtr ? INR(a.emdRefundAmount || a.emdAmount) : "—"}
                    </div>
                    {a.emdRefundUtr && (
                      <div className="text-[10px] text-slate-500">
                        {a.emdRefundUtr} | {fmtDate(a.emdRefundDate)}
                      </div>
                    )}
                  </td>

                  <td className="p-2 border text-center">
                    <Badge>{a.isAwarded ? "AWARDED" : "NOT_AWARDED"}</Badge>
                  </td>

                  <td className="p-2 border text-center">
                    <Badge>{a.emdStatus}</Badge>
                    <div className="mt-1">
                      <Badge>{isVerified(a) ? "VERIFIED" : "PENDING"}</Badge>
                    </div>
                  </td>

                  <td className="p-2 border text-center">
                    <Badge>{a.emdAccountingStatus}</Badge>
                    {isBG(a) && (
                      <div className="mt-1 text-[10px] font-black text-blue-700">
                        BG Amount: {INR(a.emdAmount)}
                      </div>
                    )}
                  </td>

                  <td className="p-2 border">
                    <div className="flex flex-wrap gap-2 justify-center">
                      <button className="btn-gray" onClick={() => editEMDDetails(a)}>
                        Edit
                      </button>

                      {!isVerified(a) && (
                        <button className="btn" onClick={() => verifyEMDPaid(a)}>
                          Verify
                        </button>
                      )}

                      {a.emdStatus !== "paid" && (
                        <button className="btn-red" onClick={() => markEMDNotPaid(a)}>
                          Not Paid
                        </button>
                      )}

                      {a.emdStatus === "paid" &&
                        isVerified(a) &&
                        !isHeldOnly(a) &&
                        !isBG(a) &&
                        !isRefunded(a) && (
                          <button className="btn-dark" onClick={() => markEMDHeld(a)}>
                            Mark Held
                          </button>
                        )}

                      {a.emdStatus === "paid" &&
                        isVerified(a) &&
                        a.isAwarded &&
                        isHeldOnly(a) &&
                        !isBG(a) &&
                        !isRefunded(a) && (
                          <button className="btn-dark" onClick={() => convertToBG(a)}>
                            Convert BG
                          </button>
                        )}

                      {a.emdStatus === "paid" &&
                        isVerified(a) &&
                        !a.isAwarded &&
                        !isBG(a) &&
                        !isRefunded(a) && (
                          <button className="btn-red" onClick={() => refundEMD(a)}>
                            Refund
                          </button>
                        )}

                      <button className="btn-red" onClick={() => correction(a)}>
                        Correction
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!rows.length && (
                <tr>
                  <td colSpan={15} className="p-8 text-center text-slate-500">
                    No EMD records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .input{border:1px solid #cbd5e1;border-radius:12px;padding:9px 12px;font-size:12px;font-weight:700;width:100%}
        .btn{background:#059669;color:white;padding:7px 10px;border-radius:9px;font-size:10px;font-weight:900}
        .btn-dark{background:#0f172a;color:white;padding:7px 10px;border-radius:9px;font-size:10px;font-weight:900}
        .btn-red{background:#dc2626;color:white;padding:7px 10px;border-radius:9px;font-size:10px;font-weight:900}
        .btn-gray{background:#e2e8f0;color:#0f172a;padding:7px 10px;border-radius:9px;font-size:10px;font-weight:900}
      `}</style>
    </div>
  );
}

function Card({ title, value, green, red }) {
  return (
    <div className="bg-white rounded-2xl border p-4 shadow-sm">
      <p className="text-xs text-slate-500">{title}</p>
      <h2
        className={`text-lg font-black mt-2 ${
          green ? "text-emerald-700" : red ? "text-red-600" : "text-slate-900"
        }`}
      >
        {value}
      </h2>
    </div>
  );
}